/**
 * PerishableInventoryDoc — long-form written explainer of the FoodPulse
 * perishable inventory management process. Rendered on
 * /food-pulse/inventory above the visual feature grid.
 *
 * Style: whitepaper / process document — plain typography, numbered
 * headings, real explanation. Deliberately text-first so a shopkeeper,
 * distributor, or ops manager can print it and hand it to staff.
 */
const GREEN  = "#0F3D2E";
const PURPLE = "#7C3AED";
const AMBER  = "#E4A140";

function H2({ children, id }) {
  return <h2 id={id} className="text-2xl font-black mt-10 mb-3 scroll-mt-24" style={{ color: GREEN }}>{children}</h2>;
}
function H3({ children }) {
  return <h3 className="text-lg font-bold mt-6 mb-2" style={{ color: GREEN }}>{children}</h3>;
}
function P({ children }) {
  return <p className="text-[15px] leading-relaxed text-gray-800 mb-3">{children}</p>;
}
function UL({ children }) {
  return <ul className="list-disc pl-6 space-y-1 text-[15px] leading-relaxed text-gray-800 mb-3">{children}</ul>;
}

export default function PerishableInventoryDoc() {
  return (
    <article className="rounded-3xl border bg-white p-6 md:p-10 mb-10" style={{ borderColor: "#E9D8B4" }} data-testid="perishable-inventory-doc">
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: PURPLE }}>Process Document · Version 1.0</div>
        <h1 className="text-3xl md:text-4xl font-black leading-tight mt-2" style={{ color: GREEN }}>
          Perishable Inventory Management on FoodPulse
        </h1>
        <p className="text-sm text-gray-600 mt-2">
          A written playbook for bakeries, grocers, farms, and food halls that need to
          track short-shelf-life stock, reduce spoilage, and move at-risk lots before
          they expire. Every step below maps to a working feature in the FoodPulse
          Owner Dashboard.
        </p>
      </div>

      {/* Table of contents */}
      <nav className="rounded-xl p-4 mb-8" style={{ background: "#FEF3C7" }}>
        <div className="text-[11px] uppercase tracking-widest font-bold mb-2" style={{ color: GREEN }}>Contents</div>
        <ol className="grid md:grid-cols-2 gap-x-6 gap-y-1 text-sm list-decimal pl-5 text-gray-800">
          <li><a href="#why" className="hover:underline">Why perishable inventory is different</a></li>
          <li><a href="#lot-first" className="hover:underline">The lot-first principle</a></li>
          <li><a href="#ledger" className="hover:underline">The append-only ledger</a></li>
          <li><a href="#lifecycle" className="hover:underline">The 6-step lifecycle</a></li>
          <li><a href="#at-risk" className="hover:underline">How "at-risk" is calculated</a></li>
          <li><a href="#promote" className="hover:underline">Promotion paths for at-risk lots</a></li>
          <li><a href="#roles" className="hover:underline">Roles &amp; access control</a></li>
          <li><a href="#outcomes" className="hover:underline">Outcomes &amp; reporting</a></li>
          <li><a href="#getting-started" className="hover:underline">Getting started</a></li>
          <li><a href="#faq" className="hover:underline">Frequently asked questions</a></li>
        </ol>
      </nav>

      <H2 id="why">1. Why perishable inventory is different</H2>
      <P>
        Conventional inventory systems assume a SKU has one price, one shelf life,
        and one location. Perishable stock breaks all three assumptions. Two crates
        of the same yogurt SKU received on different days will expire on different
        days. A pallet of tomatoes graded "B" this morning may be graded "C" by
        evening. And a batch of pastries baked at 6&nbsp;am is worth full price at
        7&nbsp;am, 30% off at 5&nbsp;pm, and worth zero (or a tax-deductible
        donation) at closing time.
      </P>
      <P>
        FoodPulse Smart Inventory is designed around this reality. Instead of
        tracking a single stock number per SKU, we track every physical arrival as
        its own <b>lot</b>, and we treat every change to that lot as an immutable
        event in a ledger. That gives you three things a normal POS cannot: expiry
        visibility, dynamic pricing, and an audit trail.
      </P>

      <H2 id="lot-first">2. The lot-first principle</H2>
      <P>
        A <b>lot</b> is one physical arrival of a product. When you receive
        20&nbsp;loaves of sourdough at 8&nbsp;am, that is one lot. When another
        20&nbsp;loaves arrive at 2&nbsp;pm the same day, that is a second lot —
        even though the SKU is identical. Every lot carries:
      </P>
      <UL>
        <li><b>GTIN / SKU / product name</b> — from a barcode scan or manual pick.</li>
        <li><b>Quantity + unit</b> — 20 loaves, 12&nbsp;kg, 6 flats, etc.</li>
        <li><b>Received-at &amp; expiry date</b> — automatically stamped, editable.</li>
        <li><b>Unit cost + supplier</b> — for margin math and cost-of-goods reporting.</li>
        <li><b>Storage location</b> — front shelf, cold room A, delivery van 2.</li>
        <li><b>Baseline photo</b> — optional, powers the AI freshness re-check.</li>
        <li><b>Condition score (0-100)</b> — starts at 100, drops as staff inspect.</li>
        <li><b>SpLink QR code</b> — auto-generated so any staff phone can scan the
          physical crate and jump straight to that lot's screen.</li>
      </UL>

      <H2 id="ledger">3. The append-only ledger</H2>
      <P>
        We never overwrite a lot's quantity. Every change is a new <b>ledger
        entry</b>, and the "current quantity" you see in the dashboard is always
        computed as the sum of every delta ever recorded on that lot. Concretely:
      </P>
      <UL>
        <li>Receive 20 loaves → <span className="font-mono text-xs">+20 · received</span></li>
        <li>Sell 3 → <span className="font-mono text-xs">−3 · sold</span></li>
        <li>Inspect &amp; mark 1 spoiled → <span className="font-mono text-xs">−1 · spoiled</span></li>
        <li>Promote 16 to a 30%-off deal → no delta, just a <span className="font-mono text-xs">promote</span> event</li>
        <li>Sell 10 through the deal → <span className="font-mono text-xs">−10 · sold_via_deal</span></li>
        <li>Donate the last 6 → <span className="font-mono text-xs">−6 · donated</span></li>
      </UL>
      <P>
        End-of-day the ledger tells you exactly what happened to that 20-loaf lot.
        This is what makes the system audit-safe — no destructive updates, no
        "someone tweaked the number." Food-safety inspectors, ESG auditors, and
        your own shrink review team can trust the numbers because the raw events
        are all there.
      </P>

      <H2 id="lifecycle">4. The 6-step lifecycle</H2>

      <H3>Step 1 — Receive lot</H3>
      <P>
        Staff open the Owner Dashboard, tap <b>Receive lot</b>, and either scan
        the case's barcode or search for the product. If the GTIN is unknown, we
        auto-stub it as a "pending_review" master product so the receiving flow
        never blocks. Expiry is defaulted from the product's shelf-life if we know
        it, otherwise the receiver picks it. A baseline photo is optional but
        recommended for anything that shows visible spoilage (produce, bakery,
        prepared foods).
      </P>

      <H3>Step 2 — Get the SpLink QR</H3>
      <P>
        On save, FoodPulse issues a SpLink short URL + QR code tied to that lot.
        Print it as a small label and stick it on the crate, tray, or shelf tag.
        Anyone on your team with a smartphone can now scan the QR to see the lot,
        adjust quantity, or trigger a promotion — no login menu-diving required.
      </P>

      <H3>Step 3 — Track &amp; inspect</H3>
      <P>
        Throughout the day, staff can:
      </P>
      <UL>
        <li>Adjust quantity (sales sync in via POS webhook, or staff enter manually).</li>
        <li>Snap a follow-up photo — Gemini estimates a freshness delta versus the
          baseline and updates the lot's condition score.</li>
        <li>Mark spoilage or damage against specific units, which drops that
          amount out of "sellable" but keeps it in the ledger for shrink reports.</li>
      </UL>

      <H3>Step 4 — Action Center surfaces at-risk lots</H3>
      <P>
        Every morning (and continuously through the day) the dashboard shows an
        <b> Action Center</b>: the top at-risk lots ranked by hours-to-expiry
        weighted by condition score. Each card carries a pre-selected recommended
        action so a manager doesn't have to think about "what should I do with
        this?" The recommendation logic is described in
        <a href="#at-risk" className="underline"> §5</a>.
      </P>

      <H3>Step 5 — One-tap promote</H3>
      <P>
        Tap the recommended button and one of the following happens instantly:
      </P>
      <UL>
        <li><b>Deal</b> — creates a public FoodPulse deal at the suggested
          discount, ending at expiry.</li>
        <li><b>Surprise Box</b> — bundles this lot with other at-risk items into
          a mystery bag at a flat price.</li>
        <li><b>Donation</b> — routes to a partner food bank and stamps the
          donation on your public Impact page.</li>
        <li><b>Transfer</b> — moves the lot to another location you operate;
          both ledgers update.</li>
      </UL>

      <H3>Step 6 — Reach shoppers</H3>
      <P>
        The moment a deal or surprise box goes live, we push it in real time via
        Server-Sent Events to every FoodPulse shopper within your delivery
        radius. It also lands on the nearby-deals page and, if opted-in, the
        weekly rescue digest. Donations show on the public Save-the-Food impact
        map. No paid distribution is required.
      </P>

      <H2 id="at-risk">5. How &quot;at-risk&quot; is calculated</H2>
      <P>
        A lot is flagged at-risk when <em>any</em> of the following hold:
      </P>
      <UL>
        <li><b>Time signal:</b> hours-to-expiry falls below the product's
          category threshold (e.g. bakery = 12h, dairy = 48h, dry goods = 30 days).</li>
        <li><b>Condition signal:</b> condition score drops below 60 following a
          staff inspection or AI freshness re-check.</li>
        <li><b>Velocity signal:</b> current sell-through rate would leave more
          than 20% of the lot unsold by expiry, based on the last 7 days of
          same-SKU velocity at this location.</li>
      </UL>
      <P>
        The recommended action follows a simple rubric: if hours-to-expiry is
        very short (&lt;&nbsp;6&nbsp;h) and quantity is low, we suggest a deep
        discount deal. If quantity is high, we suggest a Surprise Box. If the
        product is close to unsellable but still safe (bakery day-old, bruised
        produce), we suggest Donation to a partner food bank.
      </P>

      <H2 id="promote">6. Promotion paths for at-risk lots</H2>
      <UL>
        <li><b>Retail deal</b> — best for stock with 6-24 hours of life. Discount
          defaults to 30%, editable per lot.</li>
        <li><b>Surprise Box</b> — best for bundling multiple small quantities
          across SKUs. A shopper pays a flat price and gets a mixed bag.</li>
        <li><b>Between-store transfer</b> — move to a busier branch of your own
          business. Ledger updates on both sides atomically.</li>
        <li><b>Donation</b> — best when a lot is legally donatable but hard to
          sell (visually imperfect produce, day-old bread). Automatically logs a
          tax-eligible impact record + shows on your public Impact page.</li>
        <li><b>Rescue giveaway</b> — free pickup for community members if none
          of the above applies. Keeps food out of landfill and generates goodwill
          reviews on your listing.</li>
      </UL>

      <H2 id="roles">7. Roles &amp; access control</H2>
      <UL>
        <li><b>Owner</b> — full access, can promote, delete, invite staff.</li>
        <li><b>Manager</b> — receive, adjust, promote, but cannot invite or delete
          the business.</li>
        <li><b>Staff</b> — can receive lots and adjust quantities but cannot
          create promotions or donations.</li>
        <li><b>Read-only viewer</b> — great for accountants and auditors.</li>
      </UL>
      <P>
        Roles are granted per business, so a single login can be an Owner at one
        location and a Manager at another. All actions carry the actor's user id
        in the ledger.
      </P>

      <H2 id="outcomes">8. Outcomes &amp; reporting</H2>
      <P>
        The 30-day rollup on the dashboard tracks four numbers you actually care
        about:
      </P>
      <UL>
        <li><b>Active lots</b> — how much perishable stock is currently on the
          shelf.</li>
        <li><b>Closed (7&nbsp;d)</b> — lots that fully depleted in the last week
          — your throughput.</li>
        <li><b>Spoiled (30&nbsp;d)</b> — units written off — your waste.</li>
        <li><b>Donated (30&nbsp;d)</b> — units rescued — your impact.</li>
      </UL>
      <P>
        Every one of these numbers is a real-time query against the ledger, so a
        weekly review meeting starts with hard evidence rather than a spreadsheet.
      </P>

      <H2 id="getting-started">9. Getting started</H2>
      <P>
        Onboarding is designed to take one afternoon:
      </P>
      <UL>
        <li>Claim (or add) your business on FoodPulse.</li>
        <li>Print a starter pack of SpLink QR labels.</li>
        <li>Run one intake session — receive tomorrow's arrivals through the
          dashboard instead of your old spreadsheet. Nothing else changes.</li>
        <li>After 3-5 days you'll have enough velocity data for the Action
          Center to give useful recommendations.</li>
      </UL>

      <H2 id="faq">10. Frequently asked questions</H2>
      <H3>Does this replace my POS?</H3>
      <P>
        No. Smart Inventory sits next to your POS. If your POS supports webhooks
        we can subscribe to sale events so the ledger auto-updates; otherwise
        staff enter sales at the end of the day. Deal creation and donation
        logging happen inside FoodPulse.
      </P>
      <H3>What if I have multiple locations?</H3>
      <P>
        One login manages every claimed location. Each location has its own
        ledger, its own Action Center, and its own SpLink tenant scope so
        analytics are cleanly siloed.
      </P>
      <H3>Can I export the data?</H3>
      <P>
        Yes. The ledger and the rollup are exportable as CSV. Every lot's
        SpLink QR + short URL is also exportable so you can generate print
        sheets for shelf labels.
      </P>
      <H3>Is it free?</H3>
      <P>
        The Smart Inventory feature is free during preview. When we introduce
        pricing there will be a permanent free tier that covers one location
        and the essential lifecycle actions above.
      </P>

      <div className="mt-10 pt-6 border-t text-xs text-gray-500" style={{ borderColor: "#E9D8B4" }}>
        Last updated · v1.0 · Written by the FoodPulse team.
        For corrections or contributions, use the <span style={{ color: AMBER }}>Report</span> button.
      </div>
    </article>
  );
}
